'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { BlogTocEntry } from '@kiao-voice/core/blog'

/**
 * The article's contents, in the two shapes the layout needs.
 *
 * Derived from the rendered body's own headings (the API reads them back out of
 * the HTML it serves), so it cannot describe a document the reader is not
 * looking at — which is exactly what a hand-maintained contents list ends up
 * doing after the third edit.
 */

/** Distance from the viewport top that counts as「you are reading this」. */
const READING_LINE_PX = 140

/**
 * Number the h2s and leave the h3s unnumbered — the rail's numbers are the
 * reader's position index over the article's top-level sections, and an h3 whose
 * own heading text already opens with「1.」would otherwise read「2. 1. …」.
 */
export function numberTopLevel(
  entries: BlogTocEntry[]
): { entry: BlogTocEntry; number: number | null }[] {
  let counter = 0
  return entries.map((entry) => {
    if (entry.level !== 2) return { entry, number: null }
    counter += 1
    return { entry, number: counter }
  })
}

/**
 * The heading the reader is in: the LAST one that has crossed the reading line,
 * not the one that happens to be visible. A long section has no heading on
 * screen at all, and the rail must not go blank in the middle of it.
 */
function currentHeadingId(entries: BlogTocEntry[]): string | null {
  if (entries.length === 0) return null
  // The final section can be too short to ever cross the line, so the bottom of
  // the document always belongs to the last heading.
  if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 8) {
    return entries[entries.length - 1].id
  }
  let current = entries[0].id
  for (const entry of entries) {
    const element = document.getElementById(entry.id)
    if (element && element.getBoundingClientRect().top <= READING_LINE_PX) current = entry.id
  }
  return current
}

function useCurrentHeading(entries: BlogTocEntry[]): string | null {
  const [current, setCurrent] = useState<string | null>(null)
  const ticking = useRef(false)

  useEffect(() => {
    function mark() {
      ticking.current = false
      setCurrent(currentHeadingId(entries))
    }
    function onScroll() {
      if (ticking.current) return
      ticking.current = true
      window.requestAnimationFrame(mark)
    }
    mark()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', mark)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', mark)
    }
  }, [entries])

  return current
}

/**
 * Desktop rail: a hairline with a red rule marking the current section. A
 * position indicator, not a nav bar — hence no surface, border or card.
 */
export function ArticleTocRail({ entries }: { entries: BlogTocEntry[] }) {
  const { t } = useTranslation('web')
  const current = useCurrentHeading(entries)
  if (entries.length === 0) return null

  return (
    // `sticky` sits on the GRID ITEM, not on the nav inside it: a sticky box is
    // bounded by its containing block, and the nav's parent is only as tall as
    // the nav itself, so it would have nowhere to travel. The item's containing
    // block is its grid area, which spans the whole row.
    <aside className="sticky top-[92px] max-[940px]:hidden">
      <nav aria-labelledby="blog-toc-head" className="border-l border-[var(--border)]">
        <p
          id="blog-toc-head"
          className="pb-2.5 pl-4 font-[var(--font-mono)] text-[length:var(--text-2xs)] tracking-[0.14em] text-[var(--muted)]"
        >
          {t('blog.tocTitle')}
        </p>
        <ol className="m-0 list-none p-0">
          {numberTopLevel(entries).map(({ entry, number }) => (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                aria-current={current === entry.id ? 'true' : undefined}
                className={`-ml-px block border-l-2 py-[7px] pl-4 leading-[1.6] transition-colors focus-visible:rounded-[3px] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--fg)] ${
                  entry.level === 2
                    ? 'text-[length:var(--text-sm-plus)]'
                    : 'py-[5px] pl-[30px] text-[length:var(--text-xs-plus)]'
                } ${
                  current === entry.id
                    ? 'border-l-[var(--brand)] font-medium text-[var(--brand)]'
                    : 'border-l-transparent text-[var(--muted)] hover:text-[var(--fg)]'
                }`}
              >
                {number !== null && (
                  <span
                    aria-hidden="true"
                    className={`font-[var(--font-mono)] text-[length:var(--text-xs)] ${
                      current === entry.id ? 'text-[var(--brand)]' : 'text-[var(--border)]'
                    }`}
                  >
                    {number}.{' '}
                  </span>
                )}
                {entry.text}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  )
}

/**
 * Narrow viewports: the same list as the rail, expanded by default (operator
 * 2026-09-01 — the contents ARE the article's pitch, hiding them costs more
 * than the scroll they spend) but still collapsible behind the summary. Only a
 * hairline above it — the block below opens with its own rule, and a second
 * one would read as a card.
 */
export function ArticleTocCompact({ entries }: { entries: BlogTocEntry[] }) {
  const { t } = useTranslation('web')
  const [open, setOpen] = useState(true)
  if (entries.length === 0) return null

  return (
    <details
      open={open}
      onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
      className="mt-[26px] hidden border-t border-[var(--border)] max-[940px]:block max-[620px]:mt-5"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-[13px] text-[length:var(--text-base)] font-medium text-[var(--muted)] transition-colors hover:text-[var(--fg)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fg)] max-[620px]:py-3 [&::-webkit-details-marker]:hidden">
        {t('blog.tocTitle')}
        <span
          aria-hidden="true"
          className={`size-[9px] shrink-0 border-r-[1.6px] border-b-[1.6px] border-current transition-transform ${
            open ? 'translate-x-[-3px] translate-y-[-3px] rotate-[-135deg]' : 'translate-x-[-2px] translate-y-[-2px] rotate-45'
          }`}
        />
      </summary>
      {/* Deliberately unnumbered: the rail's numbers are a position index, and
          an h3 whose own text starts「1.」would end up as「2. 1. …」. */}
      <ol className="m-0 mb-[18px] list-none p-0">
        {entries.map((entry) => (
          <li key={entry.id} className={entry.level === 3 ? 'pl-[18px]' : ''}>
            <a
              href={`#${entry.id}`}
              onClick={() => setOpen(false)}
              className={`block py-[7px] leading-[1.55] hover:underline hover:underline-offset-4 focus-visible:rounded-[3px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fg)] ${
                entry.level === 3
                  ? 'text-[length:var(--text-sm-plus)] text-[var(--muted)] hover:text-[var(--fg)]'
                  : 'text-[length:var(--text-base)] text-[var(--fg)]'
              }`}
            >
              {entry.text}
            </a>
          </li>
        ))}
      </ol>
    </details>
  )
}
